/**
 * SMS 端末復旧ヘルパー (#1107).
 *
 * Firebase Auth の電話番号 link / reauth を薄くラップする。
 * - 登録時: `linkWithPhoneNumber` — SMS 検証を経て phone が Firebase user に
 *   link され、ID token に phone_number claim が載る(→ backend が claim と
 *   申告番号の一致を検証して Firestore に登録)
 * - 復旧時: `reauthenticateWithPhoneNumber` — SMS 検証成功で ID token の
 *   sign_in_provider が 'phone' になる(→ backend /devices/trust-via-sms が
 *   claim を検証して device を trusted 化)
 *
 * reCAPTCHA: Web の電話認証に必須(invisible)。Auth エミュレータ接続時は
 * 自動 bypass されるためローカルでもテスト番号で完走できる。
 */
import {
  RecaptchaVerifier,
  linkWithPhoneNumber,
  reauthenticateWithPhoneNumber,
  unlink,
} from 'firebase/auth'
import type { ConfirmationResult, User } from 'firebase/auth'
import { auth } from './firebase'

/**
 * 入力を日本の E.164 (+81...) に正規化。JP 以外 / 不正形式は null。
 * backend `_normalize_jp_phone` と同一 semantics を保つこと。
 */
export function toE164Jp(input: string): string | null {
  const digits = (input || '').replace(/[^\d+]/g, '')
  let e164: string
  if (digits.startsWith('+81')) e164 = digits
  else if (digits.startsWith('0')) e164 = '+81' + digits.slice(1)
  else return null
  return /^\+81[1-9]\d{8,9}$/.test(e164) ? e164 : null
}

/**
 * invisible reCAPTCHA verifier を生成。container は render 済みの空 div の id。
 * 呼び元が使用後に `verifier.clear()` すること(再生成時の二重 render 防止)。
 */
export function createInvisibleRecaptcha(containerId: string): RecaptchaVerifier {
  return new RecaptchaVerifier(auth, containerId, { size: 'invisible' })
}

/** 登録フロー: SMS 送信 → ConfirmationResult (confirm(code) で link 完了)。 */
export async function startPhoneLink(
  user: User,
  e164: string,
  verifier: RecaptchaVerifier,
): Promise<ConfirmationResult> {
  return linkWithPhoneNumber(user, e164, verifier)
}

/** 復旧フロー: SMS 送信 → ConfirmationResult (confirm(code) で reauth 完了)。 */
export async function startPhoneReauth(
  user: User,
  e164: string,
  verifier: RecaptchaVerifier,
): Promise<ConfirmationResult> {
  return reauthenticateWithPhoneNumber(user, e164, verifier)
}

/** 解除時の best-effort unlink (Firebase Auth 側。Firestore は backend が消す)。 */
export async function unlinkPhone(user: User): Promise<void> {
  try {
    await unlink(user, 'phone')
  } catch {
    /* 未 link 等は無視 (Firestore 側の解除が本体) */
  }
}
